import React, { useEffect, useMemo, useState } from 'react';
import useFacebookConnection from '../../hooks/useFacebookConnection';

export function getFacebookOAuthMessage(result) {
  switch (result) {
    case 'connected':
      return { type: 'success', text: 'Facebook connected. Select the Page or Pages to use with SoundScreen.' };
    case 'no_pages':
      return { type: 'warning', text: 'Facebook connected, but Meta did not return any managed Pages for this login.' };
    case 'denied':
      return { type: 'warning', text: 'Facebook authorization was cancelled or denied.' };
    case 'error':
      return { type: 'error', text: 'Facebook could not be connected. Please try again.' };
    default:
      return null;
  }
}

export function formatFacebookTask(task) {
  return String(task || '').trim().replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function formatSyncTime(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
}

function FacebookAccount({ account, action, historyWeeks, onHistoryWeeksChange, onSync }) {
  const syncing = action === `sync:${account.id}`;
  return (
    <div className="social-integration-account">
      <div>
        <strong>{account.account_name || 'Facebook Page'}</strong>
        <span>Page ID: {account.external_account_id}</span>
        <span>Last successful sync: {formatSyncTime(account.last_successful_sync_at)}</span>
        {account.last_error_code && <span>Last sync error: {account.last_error_code}</span>}
      </div>
      <div className="social-integration-account-actions">
        <span className={`social-integration-status ${account.status || 'connected'}`}>
          {account.status || 'connected'}
        </span>
        <label>
          Initial history
          <select value={historyWeeks} onChange={(event) => onHistoryWeeksChange(event.target.value)} disabled={Boolean(action)}>
            <option value="1">1 week</option>
            <option value="4">4 weeks</option>
            <option value="12">12 weeks</option>
            <option value="26">26 weeks</option>
            <option value="52">1 year</option>
            <option value="104">2 years</option>
          </select>
        </label>
        <button type="button" onClick={() => onSync(account.id, Number(historyWeeks))} disabled={Boolean(action)}>
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
    </div>
  );
}

export default function SocialIntegrations({ site }) {
  const siteId = site?.id;
  const { action, connect, disconnect, error, loading, selectPages, status, syncAccount } = useFacebookConnection(siteId);
  const [selectedPageIds, setSelectedPageIds] = useState([]);
  const [historyWeeks, setHistoryWeeks] = useState('12');
  const [notice, setNotice] = useState(() => {
    if (typeof window === 'undefined') return null;
    return getFacebookOAuthMessage(new URLSearchParams(window.location.search).get('facebookOAuth'));
  });

  const connectedPageIds = useMemo(
    () => new Set((status.accounts || []).map((account) => String(account.external_account_id))),
    [status.accounts]
  );

  useEffect(() => {
    setSelectedPageIds(
      (status.availablePages || [])
        .filter((page) => connectedPageIds.has(String(page.id)))
        .map((page) => String(page.id))
    );
  }, [connectedPageIds, status.availablePages]);

  const togglePage = (pageId) => {
    const normalizedId = String(pageId);
    setSelectedPageIds((current) => current.includes(normalizedId)
      ? current.filter((id) => id !== normalizedId)
      : [...current, normalizedId]);
  };

  const savePages = async () => {
    const saved = await selectPages(selectedPageIds);
    if (saved) setNotice({ type: 'success', text: 'Facebook Page selection saved.' });
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Facebook? Existing imported SoundScreen content will be retained.')) return;
    const disconnected = await disconnect();
    if (disconnected) setNotice({ type: 'success', text: 'Facebook disconnected.' });
  };

  const handleSync = async (accountId, requestedHistoryWeeks) => {
    const result = await syncAccount(accountId, requestedHistoryWeeks);
    if (result) {
      setNotice({
        type: 'success',
        text: `Facebook sync complete: ${result.postCount} posts and ${result.mediaCount} media items processed.`,
      });
    }
  };

  return (
    <section className="social-integration-card" aria-labelledby="facebook-integration-title">
      <div className="social-integration-heading">
        <div>
          <h3 id="facebook-integration-title">Facebook Social Content</h3>
          <p>Connect a managed Facebook Page and choose how much existing content to populate initially. Later automation will use incremental reconciliation for new posts.</p>
        </div>
        <span className={`social-integration-status ${status.connected ? 'connected' : 'disconnected'}`}>
          {status.connected ? 'Connected' : 'Not connected'}
        </span>
      </div>

      {!siteId && <p className="social-integration-message warning">Select a site before connecting Facebook.</p>}
      {!status.enabled && (
        <p className="social-integration-message warning">
          Facebook integration is not configured on this SoundScreen environment.
        </p>
      )}
      {notice && <p className={`social-integration-message ${notice.type}`}>{notice.text}</p>}
      {error && <p className="social-integration-message error">{error}</p>}
      {loading && <p className="social-integration-muted">Loading Facebook connection…</p>}

      {(status.accounts || []).length > 0 && (
        <div className="social-integration-accounts">
          <h4>Pages connected to this site</h4>
          {status.accounts.map((account) => (
            <FacebookAccount key={account.id} account={account} action={action} historyWeeks={historyWeeks} onHistoryWeeksChange={setHistoryWeeks} onSync={handleSync} />
          ))}
        </div>
      )}

      {status.connected && (status.availablePages || []).length > 0 && (
        <fieldset className="social-page-selector" disabled={action === 'select'}>
          <legend>Available Facebook Pages</legend>
          <p>Select Pages that belong to this SoundScreen site. Access details below are reported directly by Meta.</p>
          {status.availablePages.map((page) => (
            <label key={page.id} className={`social-page-option ${page.selectable ? '' : 'unavailable'}`}>
              <input
                type="checkbox"
                checked={selectedPageIds.includes(String(page.id))}
                disabled={!page.selectable}
                onChange={() => togglePage(page.id)}
              />
              <span>
                <strong>{page.name}</strong>
                <small>Page ID: {page.id}</small>
                <small>
                  Meta access: {(page.tasks || []).length
                    ? page.tasks.map(formatFacebookTask).join(', ')
                    : 'No management tasks returned'}
                </small>
              </span>
            </label>
          ))}
          <button type="button" onClick={savePages} disabled={!selectedPageIds.length || action === 'select'}>
            {action === 'select' ? 'Saving Pages…' : 'Save selected Pages'}
          </button>
        </fieldset>
      )}

      <div className="social-integration-actions">
        <button
          type="button"
          className="primary"
          onClick={connect}
          disabled={!siteId || !status.enabled || Boolean(action)}
        >
          {action === 'connect' ? 'Opening Facebook…' : status.connected ? 'Reconnect Facebook' : 'Continue with Facebook'}
        </button>
        {status.connected && (
          <button type="button" className="danger" onClick={handleDisconnect} disabled={Boolean(action)}>
            {action === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
          </button>
        )}
      </div>
      <p className="social-integration-muted">
        SoundScreen stores credentials on the server. Facebook tokens are never sent to this browser.
      </p>
    </section>
  );
}
